// frontend/src/components/plot/DisplayControls.js
import React, { useState } from 'react';
import { usePlotDetailContext } from '../../context/PlotDetailContext';

const categoryLabels = {
  weather: 'Weather & Environment',
  realtime: 'Realtime Metrics',
  accumulation: 'Accumulation and Phenology Stages',
  crop: 'Accumulation and Phenology Stages',
  telemetry: 'Device Telemetry & Health'
};

const categoryOrder = ['weather', 'realtime', 'accumulation', 'crop', 'telemetry'];

export default function DisplayControls(props) {
  const context = usePlotDetailContext() || {};

  const categoryGroups = props.categoryGroups || context.categoryGroups || {};
  const activeMetrics = props.activeMetrics || context.activeMetrics || [];
  const onToggleMetric = props.onToggleMetric || context.toggleMetric || (() => {});
  const devices = props.devices || context.devices || [];
  const visibleDeviceIds = props.visibleDeviceIds || context.visibleDeviceIds || [];

  const localTimeEnabled = props.localTimeEnabled ?? context.localTimeEnabled ?? true;
  const onToggleLocalTime = props.onToggleLocalTime || context.toggleLocalTime || (() => {});

  const forecastVisible = props.forecastVisible ?? context.forecastVisible ?? true;
  const onToggleForecast = props.onToggleForecast || context.toggleForecast || (() => {});

  const disabledSections = props.disabledSections || context.disabledSections || {};
  const onToggleSection = props.onToggleSection || context.toggleSection || (() => {});

  const [openDropdowns, setOpenDropdowns] = useState({});

  const activeProxyCount = devices.filter((d) => d.isProxy && visibleDeviceIds.includes(d.id)).length;
  const activeHardwareCount = devices.filter((d) => !d.isProxy && visibleDeviceIds.includes(d.id)).length;

  const isDevicePage = window.location.pathname.endsWith('/device');

  const toggleDropdown = (categoryKey) => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  };

  return (
    <div style={{ background: '#ffffff', borderRadius: '12px', padding: '16px 20px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>

      {/* Header & Active Data Feeds Summary */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
        <h4 style={{ margin: 0, color: '#1e293b', fontSize: '16px', fontWeight: 600 }}>
          Display Controls & Metrics Selection
        </h4>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
          <span style={{ fontWeight: 600, color: '#64748b' }}>
            Feeds: {visibleDeviceIds.length} / {devices.length}
          </span>
          <span style={{ padding: '2px 8px', borderRadius: '12px', background: activeProxyCount ? '#dbeafe' : '#f1f5f9', color: activeProxyCount ? '#1e40af' : '#94a3b8', fontWeight: 500 }}>
            Proxy: {activeProxyCount}
          </span>
          <span style={{ padding: '2px 8px', borderRadius: '12px', background: activeHardwareCount ? '#dcfce7' : '#f1f5f9', color: activeHardwareCount ? '#166534' : '#94a3b8', fontWeight: 500 }}>
            Hardware: {activeHardwareCount}
          </span>
        </div>
      </div>

      {/* Global View Preferences: Local Time & Forecast Toggles */}
      <div style={{ display: 'flex', gap: '20px', marginBottom: '14px', paddingBottom: '10px', borderBottom: '1px solid #f1f5f9', fontSize: '13px', color: '#334155' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={localTimeEnabled}
            onChange={(e) => onToggleLocalTime(e.target.checked)}
            style={{ width: '15px', height: '15px', accentColor: '#2563eb' }}
          />
          <span style={{ fontWeight: 500 }}>Use Local Timestamps</span>
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: isDevicePage ? 'not-allowed' : 'pointer', opacity: isDevicePage ? 0.6 : 1 }}>
          <input
            type="checkbox"
            checked={isDevicePage ? false : forecastVisible}
            disabled={isDevicePage}
            onChange={(e) => onToggleForecast(e.target.checked)}
            style={{ width: '15px', height: '15px', accentColor: '#2563eb', cursor: isDevicePage ? 'not-allowed' : 'pointer' }}
          />
          <span style={{ fontWeight: 500 }}>Show Forecast Data</span>
        </label>
      </div>

      {/* Dynamic Metric Selection Grid with Section Toggles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
        {categoryOrder.map((categoryKey) => {
          // Hide telemetry section on PlotDetail page
          if (!isDevicePage && categoryKey === 'telemetry') return null;
          // Hide accumulation/crop sections on DeviceDetail page
          if (isDevicePage && (categoryKey === 'accumulation' || categoryKey === 'crop')) return null;

          const groupMetrics = categoryGroups[categoryKey] || [];
          if (groupMetrics.length === 0) return null;

          const selectedCount = groupMetrics.filter((m) => activeMetrics.includes(m.type)).length;
          const isOpen = !!openDropdowns[categoryKey];
          const isSectionDisabled = !!disabledSections[categoryKey];

          return (
            <div key={categoryKey} style={{ background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', overflow: 'hidden', opacity: isSectionDisabled ? 0.6 : 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: isOpen ? '#f1f5f9' : '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold', color: '#475569', textTransform: 'uppercase' }}>
                  <input
                    type="checkbox"
                    checked={!isSectionDisabled}
                    onChange={() => onToggleSection(categoryKey)}
                    style={{ width: '14px', height: '14px', accentColor: '#2563eb' }}
                  />
                  {categoryLabels[categoryKey]}
                </label>

                <span onClick={() => toggleDropdown(categoryKey)} style={{ cursor: 'pointer', fontSize: '11px', color: '#2563eb', fontWeight: 500 }}>
                  {selectedCount}/{groupMetrics.length} active {isOpen ? '▲' : '▼'}
                </span>
              </div>

              {isOpen && (
                <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '6px', background: '#ffffff' }}>
                  {groupMetrics.map((m) => {
                    const isChecked = activeMetrics.includes(m.type);
                    return (
                      <label key={m.type} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#334155', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => onToggleMetric(m.type)}
                          disabled={isSectionDisabled}
                          style={{ accentColor: '#2563eb' }}
                        />
                        <span>{m.label} {m.unit ? `(${m.unit})` : ''}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
